import baseApi from './RootBaseApi';
import {
  MarinaSubscriptionTransactionItem,
  MarinaSubscriptionTransactionHarborOption,
  MarinaSubscriptionTransactionsListNormalized,
  MarinaSubscriptionTransactionsListParams,
  MarinaSubscriptionTransactionResponse,
} from '@/types/subscription-transactions.types';

interface MarinaSubscriptionTransactionsListApiRaw {
  total_items?: number;
  current_page?: number;
  total_pages?: number;
  data?: MarinaSubscriptionTransactionItem[];
}

export const SubscriptionTransactionsService = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getMarinaSubscriptionTransactionHarbors: builder.query<
      MarinaSubscriptionTransactionHarborOption[],
      void
    >({
      query: () => ({
        url: 'transactions/marina/harbors',
        method: 'GET',
      }),
      transformResponse: (response: unknown): MarinaSubscriptionTransactionHarborOption[] => {
        const raw = response as { data?: MarinaSubscriptionTransactionHarborOption[] };
        return (raw?.data ?? []).map((harbor) => ({
          id: String(harbor.id),
          name: String(harbor.name),
        }));
      },
      providesTags: ['SubscriptionTransactions'],
      keepUnusedDataFor: 0,
    }),

    getMarinaSubscriptionTransactionsList: builder.query<
      MarinaSubscriptionTransactionsListNormalized,
      MarinaSubscriptionTransactionsListParams
    >({
      query: (params) => ({
        url: 'transactions/marina',
        method: 'GET',
        params,
      }),
      transformResponse: (
        response: unknown,
        _meta,
        arg
      ): MarinaSubscriptionTransactionsListNormalized => {
        const raw = response as MarinaSubscriptionTransactionsListApiRaw;
        const items: MarinaSubscriptionTransactionItem[] = (
          raw?.data || []
        ).map((t) => ({
          id: String(t.id),
          type: String(t.type || 'SUBSCRIPTION'),
          amount: Number(t.amount ?? 0),
          status: String(t.status || ''),
          payment_intent_id: t.payment_intent_id ?? null,
          paytrail_payment_id: t.paytrail_payment_id ?? null,
          stripe_refund_id: t.stripe_refund_id ?? null,
          refunded_amount: Number(t.refunded_amount ?? 0),
          currency: String(t.currency || 'EUR'),
          created_at: String(t.created_at || ''),
          harbor_id: t.harbor_id ?? null,
          harbor_name: t.harbor_name ?? null,
          subscription_name: t.subscription_name ?? null,
          netvisor_invoice_id: t.netvisor_invoice_id ?? null,
          discount_applied: Boolean(t.discount_applied),
          discount_code: t.discount_code ?? null,
          discount_label: t.discount_label ?? null,
          discount_description: t.discount_description ?? null,
          discount_type: t.discount_type ?? null,
          discount_value: t.discount_value != null ? Number(t.discount_value) : null,
          discount_amount: t.discount_amount != null ? Number(t.discount_amount) : null,
          original_amount: t.original_amount != null ? Number(t.original_amount) : null,
          final_amount: t.final_amount != null ? Number(t.final_amount) : null,
          original_gross_amount:
            t.original_gross_amount != null ? Number(t.original_gross_amount) : null,
          final_gross_amount:
            t.final_gross_amount != null ? Number(t.final_gross_amount) : null,
        }));
        return {
          items,
          totalItems: Number(raw?.total_items ?? items.length),
          currentPage: Number(raw?.current_page ?? arg?.page ?? 1),
          totalPages: Number(raw?.total_pages ?? 1),
          pageLimit: Number(arg?.limit ?? items.length),
        };
      },
      providesTags: ['SubscriptionTransactions'],
      keepUnusedDataFor: 0,
    }),

    getMarinaSubscriptionTransactionDetail: builder.query<
      MarinaSubscriptionTransactionResponse,
      string
    >({
      query: (id) => ({
        url: `transactions/marina/${id}`,
        method: 'GET',
      }),
      transformResponse: (
        response: unknown
      ): MarinaSubscriptionTransactionResponse =>
        response as MarinaSubscriptionTransactionResponse,
      providesTags: ['SubscriptionTransactions'],
      keepUnusedDataFor: 0,
    }),

    downloadMarinaSubscriptionTransactionReceipt: builder.mutation<
      Blob,
      string
    >({
      query: (id) => ({
        url: `transactions/marina/${id}/receipt`,
        method: 'GET',
        responseType: 'blob',
      }),
      transformResponse: (response: unknown): Blob => response as Blob,
    }),

    emailMarinaSubscriptionTransactionReceipt: builder.mutation<
      { status: boolean; message: string },
      { data: { id: string; email: string } }
    >({
      query: ({ data }) => ({
        url: `transactions/marina/${data.id}/receipt/email`,
        method: 'POST',
        data: data,
      }),
    }),
  }),
});

export const {
  useGetMarinaSubscriptionTransactionHarborsQuery,
  useGetMarinaSubscriptionTransactionsListQuery,
  useGetMarinaSubscriptionTransactionDetailQuery,
  useDownloadMarinaSubscriptionTransactionReceiptMutation,
  useEmailMarinaSubscriptionTransactionReceiptMutation,
} = SubscriptionTransactionsService;
